import React from "react";
import { useOutletContext } from "react-router-dom";
import { Note, PageHead, Panel, RailCard, KeyValue, DeskSupport } from "@/components/portal/crm/kit";
import { DocumentsPanel } from "@/components/portal/DocumentsPanel";
import { ApplyLinksPanel } from "@/components/portal/ApplyLinksPanel";
import { NotifyPrefs } from "@/components/portal/NotifyPrefs";
import { TeamSeats } from "@/components/portal/TeamSeats";
import { SeatEarnings } from "@/components/portal/SeatEarnings";
import { FINANCE_TYPES } from "@/pages/hub/constants";
import { AdminPreviewBanner } from "./Portal";
import { ukDayYear } from "@/lib/time";

function SeatAccount({ me }) {
  return (
    <>
      <PageHead
        eyebrow={`Your seat at ${me?.company || me?.name || "the company"}`}
        title="Your seat"
        lede="You sign in with your own password and see the cases you put in. The company's commission, statements and paperwork stay with the account holder."
        testId="referrer-account-head"
      />
      <div className="grid items-start gap-7 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-7">
          <RailCard title="You" testId="referrer-seat-you">
            <KeyValue label="Name" value={me?.seat?.name || "—"} />
            <KeyValue label="Company" value={me?.company || me?.name || "—"} />
            <KeyValue label="Access" value="Agent · your own cases" />
          </RailCard>
          <SeatEarnings variant="full" />
        </div>
        <div className="space-y-4">
          <DeskSupport body="Locked out, or need something changed on your seat? Your company's account holder manages seats — or ask the desk on any of your cases." chat={false} />
          <Note>Every case you submit is attributed to {me?.company || "your company"} with your name on it, so the company can see who brought it.</Note>
        </div>
      </div>
    </>
  );
}

export default function Account() {
  const { me, reload, isAdmin, campaign, agent, floor } = useOutletContext();

  if (agent) return <SeatAccount me={me} />;

  return (
    <>
      {isAdmin && <AdminPreviewBanner />}

      <PageHead
        eyebrow="Your company on file"
        title="Paperwork & setup"
        lede="Your agreement and NDA, where payouts land, the links that credit a case to you, and what reaches you when something moves."
        testId="referrer-account-head"
      />

      <div className="grid items-start gap-7 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-7">
          <div id="documents" className="scroll-mt-24">
            <DocumentsPanel me={me} onChanged={reload} />
          </div>

          {!isAdmin && (
            <Panel title={campaign ? "Your campaign links" : "Your links and invitations"} sub={campaign ? "Every link carries your attribution. Personal invitations need a client's details, which campaign partners don't hold." : "Send a client straight to an application that is already attributed to you."} pad={false} testId="referrer-account-links">
              <div className="p-5"><ApplyLinksPanel financeTypes={FINANCE_TYPES} campaign={campaign} /></div>
            </Panel>
          )}

          {!isAdmin && !campaign && !floor && <div id="people" className="scroll-mt-24"><TeamSeats /></div>}

          {!isAdmin && floor && (
            <div id="people" className="scroll-mt-24">
              <Note>
                Your agents live on <b>The floor</b> — add a seat, set what each one takes and see who is bringing the
                cases in there. This page is your company, your paperwork and your links.
              </Note>
            </div>
          )}

          {!isAdmin && <NotifyPrefs />}
        </div>

        <div className="space-y-4">
          <RailCard title="Your company" testId="referrer-account-company">
            <KeyValue label="Company" value={me?.company || "—"} />
            <KeyValue label="Contact" value={me?.name || "—"} />
            <KeyValue label="Email" value={me?.email || "—"} />
            <KeyValue label="Phone" value={me?.phone || "—"} />
            <KeyValue label="Status" value={(me?.status || "invited").replace(/^\w/, (c) => c.toUpperCase())} tone={me?.status === "active" ? "green" : undefined} />
            <KeyValue label="Partner since" value={ukDayYear(me?.created_at, "—")} />
            <p className="mt-3 border-t border-[#EFF3F8] pt-3 text-[12.5px] leading-relaxed text-slate-500">
              Something wrong here? Message the desk and we'll change it — company details print on your statements and your media kit.
            </p>
          </RailCard>

          <RailCard title="Paperwork" testId="referrer-account-paperwork">
            <KeyValue label="Agreement" value={me?.agreement?.status === "signed" ? "Signed" : "Awaiting you"} tone={me?.agreement?.status === "signed" ? "green" : undefined} />
            <KeyValue label="Version" value={`v${me?.agreement_version || 1}`} />
            <KeyValue label="Payout details" value={me?.payout_ready ? "On file" : "Missing"} tone={me?.payout_ready ? "green" : undefined} />
          </RailCard>

          <DeskSupport body={campaign ? "Changing who signs, or your company details? Tell the desk and we'll set it up properly."
            : floor ? "Your agents are on The floor — seats, their share and the leaderboard all live there. Changing who signs? Tell the desk."
            : "Add your own agents under People & access — they get their own sign-in and see only the cases they put in. Changing who signs? Tell the desk."} />
          <Note>Bank details are held encrypted and only ever used for your own payouts.</Note>
        </div>
      </div>
    </>
  );
}
